<template>
  <div class="bg-white shadow-md rounded-xl p-4 m-4">
    <MonkeyLoader v-if="isLoading" />
    <h2 class="text-xl font-semibold mb-4 text-gray-800">
      SPK Subcon Print
    </h2>

    <form @submit.prevent="generateAndDisplayPdf" class="flex items-end gap-4">
      <div class="w-7/12">
        <label class="block text-gray-700 text-sm font-bold mb-2" for="spkNumber">
          SPK #:
        </label>
        <select
          class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          id="spkNumber" v-model="formData.spkNumber" required>
          <option value="">-- Pilih SPK # --</option>
          <option v-for="spk in spkOptions" :key="spk.id" :value="spk.id">
            {{ spk.spkNum }}
          </option>
        </select>
      </div>
      <div class="w-3/12">
        <label class="block text-gray-700 text-sm font-bold mb-2" for="spkDate">
          SPK Date:
        </label>
        <input
          class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          type="date" id="spkDate" v-model="formData.spkDate" disabled />
      </div>
      <div class="w-2/12">
        <button type="submit"
          class="bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline w-full">
          Preview
        </button>
      </div>
    </form>

    <div v-if="pdfUrl" class="mt-8 border-t-2 pt-6">
      <h3 class="text-xl font-semibold mb-4 text-gray-800">
        Print Preview
      </h3>
      <iframe :src="pdfUrl" class="w-full h-screen border rounded-md"></iframe>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Import logo images
import gspeLogo from '@/assets/images/gspe.png';
import isoLogo from '@/assets/images/iso.png';
import MonkeyLoader from '../Layout/MonkeyLoader.vue';

export default {
  components: { MonkeyLoader },
  data() {
    return {
      formData: {
        spkNumber: '',
        spkDate: '',
      },
      spkOptions: [],
      preparedData: null, // Holds the fully prepared data for PDF generation
      pdfUrl: null,       // Holds the data URL for the iframe
    };
  },
  methods: {
    async loadSpkOptions() {
      try {
        const response = await axios.get(`${window.location.origin}/api/spksubconfiles/print`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        this.spkOptions = response.data.map(spk => ({
          id: spk.Id,
          spkNum: spk.SpkNum,
        }));
      } catch (error) {
        console.error('Error fetching SPK options:', error);
        this.spkOptions = [];
      }
    },

    async prepareSpkData(spkId) {
      if (!spkId) {
        this.preparedData = null;
        this.formData.spkDate = '';
        return;
      }

      try {
        const response = await axios.get(`${window.location.origin}/api/spksubconfiles/${spkId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const data = response.data;

        this.formData.spkDate = data.SpkDate?.substring(0, 10);

        let subconName = '-', address = '-', phone = '-', fax = '-', email = '-', attn = '-', gspeuser = '-';

        // Fetch related entity and contact details
        if (data.EntityId) {
          try {
            const entityRes = await axios.get(`${window.location.origin}/api/entities/${data.EntityId}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const entity = entityRes.data.entity;
            subconName = entity.entityName || '-';
            phone = entity.entityPhone || '-';
            fax = entity.entityFax || '-';
            email = entity.entityEmail || '-';

            const [countryRes, cityRes,] = await Promise.all([
              axios.get(`${window.location.origin}/api/countries/${entity.countryId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              }),
              axios.get(`${window.location.origin}/api/cities/${entity.cityId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              })
            ]);
            address = `${entity.entityAddress} \n ${cityRes.data.cityName || ''}, ${countryRes.data.countryName || ''} ${entity.zipCode || ''}`;
          } catch (err) { console.error('Error fetching entity details:', err); }
        }

        if (data.ContactId) {
          try {
            const contactRes = await axios.get(`${window.location.origin}/api/contacts/${data.ContactId}/details`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            attn = contactRes.data.contactName || '-';
          } catch (err) { console.error('Error fetching contact details:', err); }
        }

        if (data.UserId) {
          try {
            const userRes = await axios.get(`https://meeting-backend.iotech.my.id/ext-users/${data.UserId}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            gspeuser = userRes.data.name || '-';
          } catch (err) { console.error('Error fetching user details:', err); }
        }

        // Process details in parallel
        const detailPromises = (data.TblSpksubconDetails || []).map(async (item, index) => {
          let area = '-', jobType = '-';
          if (item.PoId) {
            try {
              const projectRes = await axios.get(`https://meeting-backend.iotech.my.id/projects-bypo/${item.PoId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              });
              if (projectRes.data && projectRes.data.length > 0) {
                jobType = projectRes.data[0].name || '-';
                area = projectRes.data[0].city?.name || '-';
              }
            } catch (err) { console.error(`Error fetching project for PO ID ${item.PoId}:`, err); }
          }
          const start = new Date(item.StartDate);
          const end = new Date(item.EndDate);
          const durationDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
          return { no: index + 1, site: item.Sitename, area, jobType, price: item.Price, startDate: item.StartDate, duration: durationDays };
        });

        const formattedDetails = await Promise.all(detailPromises);

        this.preparedData = {
          no: data.SpkNum,
          date: data.SpkDate,
          name: subconName,
          address,
          phone,
          fax,
          email,
          attn,
          gspeuser,
          details: formattedDetails,
          spkJob: data.SpkJob,
          remark: data.SpkRemark || '', // Menambahkan fallback jika remark kosong
        };
      } catch (error) {
        console.error('Error fetching SPK Details:', error);
        this.preparedData = null;
        Swal.fire('Error', 'Gagal memuat detail SPK.', 'error');
      }
    },

    generateAndDisplayPdf() {
      if (!this.formData.spkNumber) {
        Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Silakan pilih nomor SPK terlebih dahulu.' });
        return;
      }
      if (!this.preparedData) {
        Swal.fire({ icon: 'error', title: 'Data Belum Siap', text: 'Data untuk SPK ini belum selesai dimuat. Silakan tunggu atau pilih kembali.' });
        return;
      }

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageHeight = doc.internal.pageSize.height;
      const margin = 15;
      let cursorY = margin;

      const drawFooter = () => {
        // ... (Fungsi drawFooter tidak perlu diubah, biarkan seperti semula)
        const footerY = pageHeight - 35;
        doc.setFontSize(8).setFont('times', 'normal');
        const address1 = 'Intercon Plaza Blok D-11, Meruya Ilir\nJakarta 11620, INDONESIA\nTelp. (62) (21) 584 5479, 586 8522/1,\nFax. (62) (21) 585 3317';
        const address2 = 'Kawasan Industri Taman Tekno\nBSD Sektor XI C Kav C-11, Tangerang\nTelp. (62) (21) 7587-9949/51\nFax. (62) (21) 7587-9952';
        doc.text(address1, margin, footerY + 5);
        doc.text(address2, 85, footerY + 5);
        doc.addImage(isoLogo, 'PNG', 165, footerY + 5, 25, 15);
      };

      const data = this.preparedData;
      const tableHead = [['No.', 'Site', 'Area', 'Project', 'Harga PO', 'Mulai', 'Waktu']];
      const tableBody = data.details.map(item => [
        item.no, item.site, item.area, item.jobType, `Rp ${Number(item.price).toLocaleString('id-ID')}`, this.formatDate(item.startDate), `${item.duration} hari`
      ]);

      doc.addImage(gspeLogo, 'PNG', margin, cursorY, 60, 30);
      cursorY += 35;
      doc.setFont('times', 'normal').setFontSize(10);

      autoTable(doc, {
        body: [['No', `: ${data.no}`], ['Tanggal', `: ${this.formatDate(data.date)}`], ['Perihal', ': SURAT PERINTAH KERJA']],
        startY: cursorY, theme: 'plain', styles: { font: 'times', fontSize: 10, cellPadding: { top: 0, bottom: 0.5 } }, columnStyles: { 0: { cellWidth: 30 } }
      });
      cursorY = doc.lastAutoTable.finalY + 3;
      autoTable(doc, {
        body: [
          ['Kepada Yth', ''],
          ['Nama Subcon', `: ${data.name}`],
          ['Alamat', `: ${data.address}`],
          ['No Telp', `: ${data.phone}`],
          ['No Fax', `: ${data.fax}`],
          ['Email', `: ${data.email}`],
          ['Attn', `: Bapak/Ibu ${data.attn}`]
        ],
        startY: cursorY, theme: 'plain', styles: { font: 'times', fontSize: 10, cellPadding: { top: 0, bottom: 0.5 } }, columnStyles: { 0: { cellWidth: 30 } }
      });
      cursorY = doc.lastAutoTable.finalY + 8;

      // --- PERUBAHAN DIMULAI DI SINI ---

      // 1. Fungsi untuk menerjemahkan SpkJob
      const getJobDescription = (jobCode) => {
        const jobTypes = {
          1: 'Survey',
          2: 'Install',
          3: 'ATP',
          4: 'Troubleshoot',
          5: 'Maintenance'
        };
        return jobTypes[jobCode] || 'Pekerjaan Sesuai Rincian'; // Teks default jika kode tidak ditemukan
      };

      const jobDescription = getJobDescription(data.spkJob);

      // 2. Menggunakan jobDescription di kalimat pembuka
      const openingText = `Dengan ini kami membuatkan Surat Perintah Kerja ${data.name} untuk pekerjaan ${jobDescription} dengan rincian biaya sebagai berikut :`;
      const splitText = doc.splitTextToSize(openingText, 180);
      doc.text(splitText, margin, cursorY, { lineHeightFactor: 1.2 });
      cursorY += (splitText.length * 3.5 * 1.2);

      autoTable(doc, {
        head: tableHead, body: tableBody, startY: cursorY, theme: 'grid',
        styles: { font: 'times', cellPadding: 1.5, fontSize: 10 },
        headStyles: { fillColor: [220, 220, 220], textColor: 0, fontStyle: 'bold', font: 'times' },
        columnStyles: {
          0: { cellWidth: 8 },
          4: { cellWidth: 25, halign: 'right' },
          5: { cellWidth: 25, halign: 'center' },
          6: { cellWidth: 20, halign: 'center' }
        },
        didDrawPage: function (data) {
          drawFooter();
          if (data.pageNumber > 1) {
            data.cursor.y = margin;
          }
        }
      });
      cursorY = doc.lastAutoTable.finalY + 8;

      const requiredSpaceForNotesAndSignature = 70;
      if (cursorY + requiredSpaceForNotesAndSignature > pageHeight - margin) {
        doc.addPage();
        cursorY = margin;
      }

      doc.setFont('times', 'normal').setFontSize(10);
      doc.text('* Remark :', margin, cursorY);
      cursorY += 4;

      // 3. Menggunakan data.remark untuk teks remark
      doc.text(data.remark, margin, cursorY);

      // --- PERUBAHAN SELESAI DI SINI ---

      cursorY += 8;
      doc.text('* Note :', margin, cursorY);
      cursorY += 4;
      const notes = ['Satu minggu setelah instalasi, ATP harus dikirimkan ke GSPE', 'ATP harus dilengkapi dg Foto, tanda tangan pihak Customer ( lengkap )', 'Pengajuan invoice harus dilengkapi materai + ATP + No. NPWP + Alamat NPWP', 'Report di email setiap hari max jam 4 sore', 'Harga PO sudah termasuk biaya Instalasi dan ATP'];
      notes.forEach((note, index) => {
        const noteText = `${index + 1}. ${note}`;
        const splitNote = doc.splitTextToSize(noteText, 180);
        doc.text(splitNote, margin + 5, cursorY, { lineHeightFactor: 1.15 });
        cursorY += (splitNote.length * 3.5 * 1.15);
      });

      if (cursorY + 30 > pageHeight - margin) {
        doc.addPage();
        cursorY = margin;
      }

      doc.setFontSize(10).text('Mengetahui', margin, cursorY + 8).text(`(${data.gspeuser})`, margin, cursorY + 26).text('Menerima', 130, cursorY + 8).text(`(${data.attn})`, 130, cursorY + 26);

      drawFooter();

      this.pdfUrl = doc.output('datauristring');
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
    },

    exitForm() {
      this.$router.back();
    },
  },
  mounted() {
    this.loadSpkOptions();
  },
  watch: {
    'formData.spkNumber'(newSpkNumber) {
      this.pdfUrl = null; // Clear previous preview
      if (newSpkNumber) {
        this.prepareSpkData(newSpkNumber);
      } else {
        this.preparedData = null;
        this.formData.spkDate = '';
      }
    },
  },
};
</script>

<style scoped>
/* Styling untuk memastikan iframe responsif */
.w-full {
  width: 100%;
}

.h-screen {
  height: 100vh;
  /* Atau tinggi spesifik seperti 800px */
}

.input-border {
  border-bottom: 2px solid #ccc;
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-top: none;
}

.input-border:focus {
  border-bottom-color: #007bff;
  box-shadow: none;
}
</style>